/**
 * /fiscal/notas-fiscais — Lista de documentos fiscais (NF-e + NFS-e + NFC-e).
 *
 * Redesign FIN-9: fiel ao mockup Financeiro V2.2 (#nfe). Casca .fin2
 * (breadcrumb + page-title + kpi-strip-v2 + table-wrap/dtable + tabchips).
 * Consome o backend fiscal existente (ADR-0032 + M6/M12/M14) — sem mudar lógica.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiFilePlus, FiSettings } from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { formatarReais } from "../../../services/financeiro";
import {
  listarNotasFiscais,
  SITUACAO_NF_LABEL,
  SITUACAO_NF_TONE,
  TIPO_DOC_FISCAL_LABEL,
} from "../../../services/fiscal";
import { PageContainer } from "../../../styles/shared";

import CriarNfceForm from "./CriarNfceForm";
import CriarNfeForm from "./CriarNfeForm";
import CriarNfseForm from "./CriarNfseForm";

// Filtro de tipo (client-side; o backend lista todos os tipos juntos).
const TABS_TIPO = [
  { id: "", label: "Todos" },
  { id: "NFE", label: "NF-e" },
  { id: "NFSE", label: "NFS-e" },
  { id: "NFCE", label: "NFC-e" },
];

const TIPO_TONE = { NFE: "neutral", NFSE: "info", NFCE: "info" };

function fmtDataHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function chaveTrunc(chave) {
  if (!chave) return "—";
  return `${chave.slice(0, 8)}…${chave.slice(-6)}`;
}

export default function NotasFiscaisLista() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  // hasPermission já faz bypass de Master.
  const podeEmitirNota = hasPermission("fiscal:emitir");
  const podeConfigurarFiscal = hasPermission("fiscal:configurar");

  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabTipo, setTabTipo] = useState("");
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [criarNfeAberto, setCriarNfeAberto] = useState(false);
  const [criarNfseAberto, setCriarNfseAberto] = useState(false);
  const [criarNfceAberto, setCriarNfceAberto] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await listarNotasFiscais(request, {});
      setNotas(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar notas fiscais.");
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  // KPIs sempre sobre o conjunto inteiro (não o filtrado).
  const kpis = useMemo(() => {
    const por = {};
    let valorEmitido = 0;
    for (const n of notas) {
      por[n.situacao] = (por[n.situacao] || 0) + 1;
      if (n.situacao === "EMITIDA") valorEmitido += Number(n.valor_total || 0);
    }
    return {
      emitidas: por.EMITIDA || 0,
      pendentes: por.PENDENTE || 0,
      canceladas: por.CANCELADA || 0,
      problemas: (por.DENEGADA || 0) + (por.ERRO || 0),
      valorEmitido,
    };
  }, [notas]);

  const visiveis = useMemo(() => notas.filter((n) => {
    const tipo = n.tipo || "NFE";
    if (tabTipo && tipo !== tabTipo) return false;
    if (filtroSituacao && n.situacao !== filtroSituacao) return false;
    return true;
  }), [notas, tabTipo, filtroSituacao]);

  const onCreated = (nova) => navigate(`/fiscal/notas-fiscais/${nova.id}`);

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Fiscal</span><span className="crumb-sep">/</span>
            <span className="active">Notas Fiscais</span>
          </div>
          <h1 className="page-title">Notas Fiscais Eletrônicas</h1>
          <div className="page-sub">
            {kpis.emitidas} emitida(s) · {formatarReais(kpis.valorEmitido)} · emissão via gateway (NF-e / NFS-e / NFC-e)
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {podeConfigurarFiscal && (
            <button type="button" className="btn btn--ghost" onClick={() => navigate("/fiscal/configuracao")}>
              <FiSettings size={14} aria-hidden="true" /> Configuração
            </button>
          )}
          {podeEmitirNota && (
            <>
              <button type="button" className="btn btn--secondary" onClick={() => setCriarNfseAberto(true)}>
                <FiFilePlus size={14} aria-hidden="true" /> NFS-e
              </button>
              <button type="button" className="btn btn--secondary" onClick={() => setCriarNfceAberto(true)}>
                <FiFilePlus size={14} aria-hidden="true" /> NFC-e
              </button>
              <button type="button" className="btn btn--primary" onClick={() => setCriarNfeAberto(true)}>
                <FiFilePlus size={14} aria-hidden="true" /> Emitir NF-e
              </button>
            </>
          )}
        </div>
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Emitidas</div><div className="val" style={{ color: "var(--success-text)" }}>{kpis.emitidas}</div><div className="meta">autorizadas no SEFAZ</div></div>
        <div className="kpi-v2"><div className="lbl">Pendentes</div><div className="val" style={{ color: "var(--warning-text)" }}>{kpis.pendentes}</div><div className="meta">aguardando emissão</div></div>
        <div className="kpi-v2"><div className="lbl">Canceladas</div><div className="val">{kpis.canceladas}</div><div className="meta">dentro da janela 24h</div></div>
        <div className="kpi-v2"><div className="lbl">Denegadas / erro</div><div className="val" style={{ color: kpis.problemas ? "var(--danger-text)" : undefined }}>{kpis.problemas}</div><div className="meta">requerem atenção</div></div>
        <div className="kpi-v2"><div className="lbl">Valor emitido</div><div className="val">{formatarReais(kpis.valorEmitido)}</div><div className="meta">notas autorizadas</div></div>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div className="table-wrap">
        <div className="flex between" style={{ alignItems: "center", padding: "12px 18px", borderBottom: "1px solid var(--border)", gap: 12, flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {TABS_TIPO.map((t) => (
              <div key={t.id} className={"tabchip" + (tabTipo === t.id ? " active" : "")}
                   role="button" tabIndex={0} onClick={() => setTabTipo(t.id)}
                   onKeyDown={(e) => e.key === "Enter" && setTabTipo(t.id)}>{t.label}</div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <select className="select" style={{ width: 170 }} value={filtroSituacao}
                    onChange={(e) => setFiltroSituacao(e.target.value)}>
              <option value="">Todas as situações</option>
              <option value="PENDENTE">Pendente</option>
              <option value="EMITIDA">Emitida</option>
              <option value="CANCELADA">Cancelada</option>
              <option value="DENEGADA">Denegada</option>
              <option value="ERRO">Erro</option>
            </select>
            <span className="tiny mono muted">{visiveis.length} NOTAS</span>
          </div>
        </div>
        <table className="dtable">
          <colgroup>
            <col style={{ width: 116 }} /><col style={{ width: 84 }} /><col style={{ width: 120 }} />
            <col /><col style={{ width: 150 }} /><col style={{ width: 90 }} />
            <col className="col-money" /><col style={{ width: 110 }} />
          </colgroup>
          <thead><tr>
            <th>Número</th><th>Tipo</th><th>Emissão</th><th>Cliente</th>
            <th>Chave SEFAZ</th><th>Pedido</th>
            <th className="num" style={{ textAlign: "right" }}>Valor</th><th>Status</th>
          </tr></thead>
          <tbody>
            {visiveis.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 0 }}><div className="empty"><div className="msg">
                {loading ? "Carregando…" : "Nenhuma nota fiscal neste filtro. Emita uma NF-e, NFS-e ou NFC-e a partir de um pedido entregue."}
              </div></div></td></tr>
            )}
            {visiveis.map((n) => {
              const tipo = n.tipo || "NFE";
              const perigo = n.situacao === "DENEGADA" || n.situacao === "ERRO";
              const pendente = n.situacao === "PENDENTE";
              const bg = perigo ? "var(--danger-bg)" : pendente ? "var(--warning-bg)" : undefined;
              return (
                <tr key={n.id} className="rowlink" style={bg ? { background: bg } : undefined}
                    onClick={() => navigate(`/fiscal/notas-fiscais/${n.id}`)}>
                  <td className="mono">{n.numero}</td>
                  <td><span className={"badge no-dot " + (TIPO_TONE[tipo] || "neutral")}>{(TIPO_DOC_FISCAL_LABEL[tipo] || "NF-e").split(" ")[0]}</span></td>
                  <td className="mono">{fmtDataHora(n.emitida_em || n.created_at)}</td>
                  <td className="ell" title={n.cliente_nome}>{n.cliente_nome || "—"}</td>
                  <td className="mono" title={n.chave_acesso || ""} style={{ color: "var(--ink-3)" }}>{chaveTrunc(n.chave_acesso)}</td>
                  <td className="mono">{n.pedido_numero || "—"}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{formatarReais(n.valor_total)}</td>
                  <td><span className={"badge no-dot " + (SITUACAO_NF_TONE[n.situacao] || "neutral")}>{SITUACAO_NF_LABEL[n.situacao] || n.situacao}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <SlideOverPanel open={criarNfeAberto} onClose={() => setCriarNfeAberto(false)}
                      title="Emitir NF-e" subtitle="Nota fiscal de mercadoria (modelo 55)" width={560}>
        {criarNfeAberto && (
          <CriarNfeForm onCreated={(n) => { setCriarNfeAberto(false); onCreated(n); }}
                        onCancel={() => setCriarNfeAberto(false)} />
        )}
      </SlideOverPanel>

      <SlideOverPanel open={criarNfseAberto} onClose={() => setCriarNfseAberto(false)}
                      title="Emitir NFS-e" subtitle="Nota fiscal de serviço (município)" width={560}>
        {criarNfseAberto && (
          <CriarNfseForm onCreated={(n) => { setCriarNfseAberto(false); onCreated(n); }}
                         onCancel={() => setCriarNfseAberto(false)} />
        )}
      </SlideOverPanel>

      <SlideOverPanel open={criarNfceAberto} onClose={() => setCriarNfceAberto(false)}
                      title="Emitir NFC-e" subtitle="Nota fiscal de consumidor (modelo 65)" width={560}>
        {criarNfceAberto && (
          <CriarNfceForm onCreated={(n) => { setCriarNfceAberto(false); onCreated(n); }}
                         onCancel={() => setCriarNfceAberto(false)} />
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}
